import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Plus, X } from 'lucide-react'
import { useProfileQuery } from 'v2/pages/settings/queries'
import z from 'zod'
import { AudioPrompt } from '../components/audio-prompt'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { promptData } from '../config/nodes.config'
import { v2validations } from '../config/validation.config'
import type { GetInputNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = GetInputNodeData['config']

// Node-level event callbacks for collect-response (legacy PHLO). SER-4772.
const getInputEventTypes = [{ value: 'digits', label: 'Digits' }]

const fields = (nodeVars: string[], hsEnabled: boolean): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).get_input
  const config: GenericFieldConfig<FormValues> = [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.shape.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'input_timeout',
      label: 'Input Timeout',
      zodValidation: validations.shape.input_timeout,
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
        min: 0,
      },
    },
    {
      name: 'interrupt',
      label: 'Interrupt audio on input',
      componentType: 'checkbox',
      zodValidation: validations.shape.interrupt,
    },
    {
      name: 'max_input_size',
      label: 'Max Input Size',
      zodValidation: validations.shape.max_input_size,
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Characters</span>,
        min: 0,
      },
    },
    {
      name: 'prompt',
      defaultNewValue: promptData,
      type: 'array',
      className: 'relative',
      zodValidation: z.array(z.any()).min(1, 'At least one prompt is required'), // A UI issue would occur if we use the zodValidation from the schema
      removeItemButton: <X className="w-4 h-4 absolute top-1 right-0 text-tertiary" />,
      addItemButton: (props) => (
        <div>
          <Button variant="ghost" className="form-button" {...props}>
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </div>
      ),
      subFields: [
        {
          name: '',
          type: 'sub-item',
          // @ts-expect-error - Deepkeys won't support this
          component: (props) => <AudioPrompt {...props} nodeVars={nodeVars} />,
          zodValidation: validations.shape.prompt.element,
          eventDataExtractor: (data) => data as Record<string, unknown>,
        },
      ],
    },
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={getInputEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
  if (hsEnabled) {
    config.push({
      name: 'handoff_context_reference',
      label: 'Receive AI transfer reference',
      componentType: 'switch',
      zodValidation: validations.shape.handoff_context_reference,
      inputProps: {
        description:
          'For transfers without SIP headers. Collects 39 digits sent automatically by the AI. Bind this Flow to the source and connect both outputs to Assign to human with Context enabled. Max Input Size is fixed at 39 in this mode.',
      },
    })
  }
  return config
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const GetInputSidebarComponent = (props: SidebarComponentProps<GetInputNodeData>) => {
  const { nodeVars } = props
  const { data: profile } = useProfileQuery()
  const formFields = fields(
    nodeVars,
    profile?.organizations?.[0]?.human_specialist_enabled === true,
  )

  return <GenericSidebarComponent fields={formFields} {...props} />
}
